import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  ArrowLeft,
  Calendar,
  ChevronRight,
  Clock,
  Copy,
  MessageCircle,
  Mail,
  RefreshCw,
  Send,
  ShieldAlert,
  FileText,
  Users,
  History,
  CheckCircle2,
  Save,
  Upload,
  FileBadge,
  Trash2,
  Printer,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api, hasRole } from "../utils/auth";
import {
  getKasusAPI,
  updateKasusAPI,
  deleteKasusAPI,
  STATUS_TL,
  STATUS_TL_LABEL,
  recalcRekomendasi,
  progressKasus,
  JENIS_TL,
} from "../utils/auditStorage";
import { loadHariLibur } from "../utils/hariLiburStorage";
import { hitungDeadline, sisaHariKerja, formatTanggalID, todayIso } from "../utils/hariKerja";
import { run60HKEngine } from "../utils/notifEngine60HK";
import { buildEmailDraft } from "../utils/emailDraft";
import { buildWATemplate, buildWAPenyerahanTerperiksa, buildWAPenyerahanTim, JENIS_LABELS } from "../data/templatePesan";
import { kirimWA } from "../utils/fonnte";
import { Toaster, toast } from "../components/ui/sonner";

const input = "block w-full rounded-md border border-gray-300 bg-white px-2.5 py-2 text-[13px] focus:border-[#1e3a5f] focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20";

const STATUS_TL_COLOR = {
  MENUNGGU: "bg-gray-100 text-gray-700 border-gray-200",
  DALAM_PROSES: "bg-blue-100 text-blue-800 border-blue-200",
  SELESAI_TEPAT: "bg-emerald-100 text-emerald-800 border-emerald-200",
  SELESAI_LAMBAT: "bg-amber-100 text-amber-800 border-amber-200",
  MELEWATI_BATAS: "bg-slate-900 text-white border-slate-900",
  ESKALASI: "bg-red-100 text-red-800 border-red-200",
};

const TABS = [
  { key: "ringkasan", label: "Ringkasan", icon: FileText },
  { key: "penyerahan", label: "Penyerahan & Bukti", icon: FileBadge },
  { key: "rekomendasi", label: "Rekomendasi & TL", icon: CheckCircle2 },
  { key: "terperiksa", label: "Pihak Terlibat", icon: Users },
  { key: "kirim_notif", label: "Kirim Notif", icon: Send },
  { key: "riwayat", label: "Riwayat Notifikasi", icon: History },
];

export default function AuditInvestigasiDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [kasus, setKasus] = useState(null);
  const [loadingKasus, setLoadingKasus] = useState(true);
  const [hariLibur] = useState(() => loadHariLibur());
  const [users, setUsers] = useState([]);
  const [tab, setTab] = useState("ringkasan");
  const [draftModal, setDraftModal] = useState(null);

  // Form untuk Kirim Notifikasi Manual / Berkala
  const [notifForm, setNotifForm] = useState({
    tipe: "MANUAL", // "MANUAL" atau "BERKALA"
    targetTim: true,
    targetTerperiksa: true,
    pesan: "",
  });

  useEffect(() => {
    let mounted = true;
    getKasusAPI(id)
      .then((data) => { if (mounted) { setKasus(data); setLoadingKasus(false); } })
      .catch(() => { if (mounted) setLoadingKasus(false); });
    api.get("/users").then((r) => { if (mounted) setUsers(r.data); }).catch(() => {});
    return () => { mounted = false; };
  }, [id]);

  const canEditTL = hasRole(user, "IRBAN", "DALNIS", "KETUA_TIM");
  const canEditKontak = hasRole(user, "IRBAN", "DALNIS");
  const canSendManual = hasRole(user, "IRBAN", "DALNIS");

  const sisaHK = useMemo(
    () => (kasus?.deadlineTL60HK ? sisaHariKerja(todayIso(), kasus.deadlineTL60HK, hariLibur) : 0),
    [kasus, hariLibur],
  );

  const persist = async (nextKasus) => {
    setKasus(nextKasus);
    try {
      await updateKasusAPI(nextKasus.id, nextKasus);
    } catch (err) {
      toast.error("Gagal menyimpan ke server: " + (err?.response?.data?.detail || err.message));
    }
  };

  const refreshEngine = async () => {
    toast.info("Menjalankan Engine 60HK untuk kasus ini...");
    const { kasusListUpdated } = run60HKEngine({ kasusList: [kasus], users, hariLibur });
    const updatedKasusInfo = kasusListUpdated[0];
    await persist(updatedKasusInfo);

    if (updatedKasusInfo) {
      const pendingWAs = updatedKasusInfo.riwayatNotifikasi.filter(r => r.statusKirim === "PENDING" && r.media === "WA");
      if (pendingWAs.length > 0) {
        toast.info(`Mengirim ${pendingWAs.length} notifikasi WA otomatis... (interval 30 dtk)`);
        for (const entry of pendingWAs) {
           await sendWAEntryAuto(entry);
           await new Promise(resolve => setTimeout(resolve, 30000));
        }
        toast.success("Semua antrean WA otomatis telah terkirim.");
      } else {
        toast.success("Engine 60HK selesai (Tidak ada pesan WA baru).");
      }
    }
  };

  const sendWAEntryAuto = async (entry) => {
    const res = await kirimWA({
      target: entry.target,
      message: entry.pesanTerkirim,
      caseId: kasus.id,
      jenis: entry.jenis,
    });

    setKasus(prev => {
      const nextKasus = {
        ...prev,
        riwayatNotifikasi: prev.riwayatNotifikasi.map(r =>
          r.id === entry.id
            ? { ...r, statusKirim: res.ok ? "TERKIRIM" : "GAGAL", errorMessage: res.ok ? null : res.error, tanggalKirim: new Date().toISOString() }
            : r
        )
      };
      updateKasusAPI(nextKasus.id, nextKasus).catch(() => {});
      return nextKasus;
    });
  };

  const handleSetPenyerahan = async (tanggal) => {
    if (!tanggal) return;
    const deadline = hitungDeadline(tanggal, 60, hariLibur);

    if (!kasus.notifPenyerahanSent) {
      toast.info("Mengirim notifikasi otomatis...");
      let successCount = 0;

      for (const t of kasus.terperiksa) {
        if (t.noWA) {
          const pesan = buildWAPenyerahanTerperiksa({ kasus: { ...kasus, tanggalPenyerahanLaporan: tanggal, deadlineTL60HK: deadline }, terperiksa: t });
          await kirimWA({ target: t.noWA, message: pesan, caseId: kasus.id, jenis: "PENYERAHAN" });
          successCount++;
        }
      }

      for (const uid of kasus.timPemeriksa) {
        const u = users.find(x => x.id === uid);
        if (u && u.noWA) {
           const pesanTim = buildWAPenyerahanTim({ kasus: { ...kasus, tanggalPenyerahanLaporan: tanggal, deadlineTL60HK: deadline }, namaAnggota: u.name, roleAnggota: u.role });
           await kirimWA({ target: u.noWA, message: pesanTim, caseId: kasus.id, jenis: "PENYERAHAN_TIM" });
           successCount++;
           await new Promise(resolve => setTimeout(resolve, 30000));
        }
      }

      toast.success(`Notifikasi terkirim ke ${successCount} kontak terkait.`);
    }

    const nextKasus = {
      ...kasus,
      tanggalPenyerahanLaporan: tanggal,
      deadlineTL60HK: deadline,
      notifPenyerahanSent: true
    };
    await persist(nextKasus);
    toast.success("Tanggal penyerahan dan deadline 60 HK diperbarui");
  };

  const handleUploadBukti = (rekId, e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (event) => {
       const newBukti = {
         id: Date.now().toString(),
         name: file.name,
         type: file.type,
         dataUrl: event.target.result,
         uploadedAt: new Date().toISOString()
       };
       const targetRek = kasus.rekomendasi.find(r => r.id === rekId);
       updateRek(rekId, { buktiFiles: [...(targetRek.buktiFiles || []), newBukti] });
       toast.success("Bukti berhasil diunggah");
    };
    reader.readAsDataURL(file);
  };

  const hapusBukti = (rekId, buktiId) => {
    const targetRek = kasus.rekomendasi.find(r => r.id === rekId);
    updateRek(rekId, { buktiFiles: targetRek.buktiFiles.filter(b => b.id !== buktiId) });
  };

  const updateRek = async (rekId, patch) => {
    const nextKasus = {
      ...kasus,
      rekomendasi: kasus.rekomendasi.map((r) =>
        r.id === rekId ? recalcRekomendasi({ ...r, ...patch }) : r,
      ),
    };
    await persist(nextKasus);
    toast.success("Rekomendasi diperbarui");
  };

  const updateRiwayat = async (entryId, patch) => {
    const nextKasus = {
      ...kasus,
      riwayatNotifikasi: kasus.riwayatNotifikasi.map((r) =>
        r.id === entryId ? { ...r, ...patch } : r,
      ),
    };
    await persist(nextKasus);
  };

  const handleDeleteNotif = async (entryId) => {
    const nextKasus = {
      ...kasus,
      riwayatNotifikasi: kasus.riwayatNotifikasi.filter(r => r.id !== entryId)
    };
    await persist(nextKasus);
    toast.success("Notifikasi berhasil dihapus");
  };

  const handleExportPDF = (r) => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      return toast.error("Pop-up diblokir oleh browser. Izinkan pop-up untuk mencetak PDF.");
    }
    
    const formattedMessage = (r.pesanTerkirim || "").replace(/\n/g, '<br>');
    const tglBuat = new Date(r.tanggal).toLocaleString('id-ID');
    const tglKirim = r.tanggalKirim ? new Date(r.tanggalKirim).toLocaleString('id-ID') : '-';
    
    const htmlContent = `
      <html>
        <head>
          <title>Bukti Notifikasi - ${r.penerima}</title>
          <style>
            body { font-family: 'Segoe UI', Arial, sans-serif; padding: 40px; color: #333; line-height: 1.6; max-width: 800px; margin: 0 auto; }
            .header { border-bottom: 2px solid #1e3a5f; padding-bottom: 15px; margin-bottom: 30px; }
            .header h2 { margin: 0; color: #1e3a5f; }
            .header p { margin: 5px 0 0 0; color: #666; font-size: 14px; }
            .meta-box { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 20px; margin-bottom: 30px; }
            .meta-row { display: flex; margin-bottom: 10px; }
            .meta-label { width: 150px; font-weight: bold; color: #475569; font-size: 14px; }
            .meta-value { flex: 1; font-size: 14px; }
            .message-box { border: 1px solid #cbd5e1; border-radius: 8px; padding: 20px; background: #fff; }
            .message-title { font-weight: bold; margin-bottom: 15px; color: #1e3a5f; }
            .footer { margin-top: 50px; text-align: center; font-size: 12px; color: #94a3b8; }
            @media print {
              body { -webkit-print-color-adjust: exact; padding: 0; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h2>Bukti Pengiriman Notifikasi</h2>
            <p>Sistem Irbansus - Inspektorat Kabupaten Tasikmalaya</p>
          </div>
          
          <div class="meta-box">
            <div class="meta-row"><div class="meta-label">Nomor LHP</div><div class="meta-value">: ${kasus.nomorLHP || '-'}</div></div>
            <div class="meta-row"><div class="meta-label">Jenis Pesan</div><div class="meta-value">: ${r.jenis}</div></div>
            <div class="meta-row"><div class="meta-label">Penerima</div><div class="meta-value">: ${r.penerima}</div></div>
            <div class="meta-row"><div class="meta-label">Target (${r.media})</div><div class="meta-value">: ${r.target}</div></div>
            <div class="meta-row"><div class="meta-label">Status Kirim</div><div class="meta-value">: ${r.statusKirim}</div></div>
            <div class="meta-row"><div class="meta-label">Waktu Dibuat</div><div class="meta-value">: ${tglBuat}</div></div>
            <div class="meta-row"><div class="meta-label">Waktu Terkirim</div><div class="meta-value">: ${tglKirim}</div></div>
          </div>
          
          <div class="message-box">
            <div class="message-title">Isi Pesan:</div>
            <div>${formattedMessage}</div>
          </div>
          
          <div class="footer">
            <p>Dicetak pada: ${new Date().toLocaleString('id-ID')}</p>
          </div>
          <script>
            window.onload = function() {
              setTimeout(function() {
                window.print();
                window.close();
              }, 500);
            }
          </script>
        </body>
      </html>
    `;
    
    printWindow.document.write(htmlContent);
    printWindow.document.close();
  };

  const sendWAEntry = async (entry) => {
    const res = await kirimWA({
      target: entry.target,
      message: entry.pesanTerkirim,
      caseId: kasus.id,
      jenis: entry.jenis,
    });
    updateRiwayat(entry.id, {
      statusKirim: res.ok ? "TERKIRIM" : "GAGAL",
      errorMessage: res.ok ? null : res.error,
      tanggalKirim: new Date().toISOString(),
    });
    if (res.ok) toast.success(`WA terkirim ke ${entry.penerima}`);
    else toast.error(`WA gagal: ${res.error}`);
  };

  const handleGenerateNotif = async (e) => {
    e.preventDefault();
    if (!notifForm.targetTim && !notifForm.targetTerperiksa) {
      return toast.error("Pilih setidaknya satu target penerima.");
    }
    
    let entries = [];
    const now = new Date().toISOString();

    const addEntry = (penerima, targetWA, media, text, jenisOverride = "MANUAL", statusKirim = "PENDING") => {
      if (!targetWA) return;
      entries.push({
        id: `rn-${kasus.id}-CUST-${Date.now().toString(36)}-${Math.random().toString(36).slice(2,6)}`,
        tanggal: now,
        jenis: jenisOverride,
        penerima,
        target: targetWA,
        media,
        statusKirim,
        pesanTerkirim: text,
      });
    };

    // Helper untuk mengumpulkan target
    const targetWAs = [];
    if (notifForm.targetTim) {
      kasus.timPemeriksa.forEach(uid => {
        const u = users.find(x => x.id === uid);
        if (u && u.noWA) targetWAs.push({ nama: u.name, wa: u.noWA, role: u.role });
      });
    }
    if (notifForm.targetTerperiksa) {
      kasus.terperiksa.forEach(t => {
        if (t.noWA) targetWAs.push({ nama: t.namaPejabat || t.namaInstansi, wa: t.noWA, role: "Terperiksa" });
      });
    }

    if (targetWAs.length === 0) {
      return toast.error("Tidak ada nomor WA target yang valid.");
    }

    if (notifForm.tipe === "MANUAL") {
      if (!notifForm.pesan.trim()) return toast.error("Pesan tidak boleh kosong.");
      targetWAs.forEach(t => {
        addEntry(`${t.nama} (${t.role})`, t.wa, "WA", notifForm.pesan, "MANUAL_CUSTOM");
      });
      toast.success(`Berhasil membuat ${entries.length} notifikasi manual. Buka tab Riwayat untuk mengirim.`);
    } else {
      // Berkala 3 Kali
      const intervalMsg = [
        "Pemberitahuan Berkala 1/3: Mohon sampaikan progres awal tindak lanjut.",
        "Pemberitahuan Berkala 2/3: Separuh waktu telah berjalan, segera penuhi dokumen tindak lanjut.",
        "Pemberitahuan Berkala 3/3: Waktu hampir habis, mohon segera selesaikan tindak lanjut sebelum batas 60 Hari Kerja."
      ];
      targetWAs.forEach(t => {
        intervalMsg.forEach((msg, idx) => {
          const text = `[SISTEM IRBAN - BERKALA ${idx+1}/3]\n\nYth. ${t.nama},\nLHP No: ${kasus.nomorLHP}\nJudul: ${kasus.judulKasus}\n\n${msg}`;
          addEntry(`${t.nama} (${t.role})`, t.wa, "WA", text, `BERKALA_${idx+1}`);
        });
      });
      toast.success(`Berhasil menjadwalkan ${entries.length} notifikasi berkala. Buka tab Riwayat untuk mengirim.`);
    }

    if (entries.length > 0) {
      const nextKasus = {
        ...kasus,
        riwayatNotifikasi: [...(kasus.riwayatNotifikasi || []), ...entries],
      };
      await persist(nextKasus);
      setNotifForm({ ...notifForm, pesan: "" });
    }
  };

  const openDraft = (entry) => {
    const terperiksa = kasus.terperiksa.find(
      (t) => t.namaPejabat && entry.penerima?.includes(t.namaPejabat),
    ) || kasus.terperiksa[0];
    const draft = buildEmailDraft({
      jenis: entry.jenis,
      kasus,
      terperiksa: { ...terperiksa, email: entry.target },
      sisaHK,
    });
    setDraftModal({ entry, draft });
  };

  if (loadingKasus) {
    return (
      <div className="flex items-center justify-center py-20 text-[13px] text-slate-500">
        Memuat kasus…
      </div>
    );
  }

  if (!kasus) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-8 text-center">
        <div className="text-[13px] font-semibold text-slate-900">Kasus tidak ditemukan.</div>
        <Link to="/audit-investigasi" className="mt-3 inline-flex text-[12px] font-semibold text-[#1e3a5f]">
          ← Kembali ke daftar
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="audit-detail-page">
      <Toaster position="top-right" richColors />

      <div>
        <button
          type="button"
          onClick={() => navigate("/audit-investigasi")}
          className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-slate-600 hover:text-[#1e3a5f]"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Kembali
        </button>
      </div>

      <div className="flex flex-col gap-3 border-b border-gray-200 pb-5 md:flex-row md:items-end md:justify-between">
        <div>
          <div className="inline-flex items-center gap-2">
            <span className="font-mono text-[11px] text-slate-500">{kasus.nomorLHP}</span>
            <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold ${
              kasus.statusKeseluruhan === "SELESAI" ? "border-emerald-200 bg-emerald-50 text-emerald-700"
              : kasus.statusKeseluruhan === "MELEWATI_BATAS" ? "border-slate-900 bg-slate-900 text-white"
              : "border-blue-200 bg-blue-50 text-blue-700"
            }`}>
              {kasus.statusKeseluruhan}
            </span>
          </div>
          <h1 className="mt-1 font-display text-xl font-bold text-slate-900 md:text-2xl">{kasus.judulKasus}</h1>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={refreshEngine} className="inline-flex h-9 items-center gap-1.5 rounded-md border border-gray-200 bg-white px-3 text-[12px] font-semibold" data-testid="run-engine-btn">
            <RefreshCw className="h-3.5 w-3.5" /> Run Engine 60HK
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 overflow-x-auto border-b border-gray-200" data-testid="audit-tabs">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`audit-tab-${t.key}`}
              className={`inline-flex items-center gap-1.5 border-b-2 px-4 py-2.5 text-[12.5px] font-semibold ${
                active ? "border-[#1e3a5f] text-[#1e3a5f]" : "border-transparent text-slate-500 hover:text-slate-800"
              }`}
            >
              <Icon className="h-3.5 w-3.5" /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "ringkasan" ? (
        <section className="grid grid-cols-1 gap-5 md:grid-cols-3" data-testid="tab-ringkasan">
          <div className="md:col-span-2 rounded-lg border border-gray-200 bg-white p-5">
            <h3 className="font-display text-base font-semibold text-slate-900">Informasi LHP</h3>
            <dl className="mt-3 space-y-2 text-[12.5px]">
              <Row label="Nomor LHP">{kasus.nomorLHP}</Row>
              <Row label="Tanggal LHP">{formatTanggalID(kasus.tanggalLHP)}</Row>
              <Row label="Tgl Penyerahan">{kasus.tanggalPenyerahanLaporan ? formatTanggalID(kasus.tanggalPenyerahanLaporan) : <span className="text-red-600 font-semibold">Belum diserahkan</span>}</Row>
              <Row label="Deadline (60 HK)">{kasus.deadlineTL60HK ? formatTanggalID(kasus.deadlineTL60HK) : "—"}</Row>
              <Row label="Status">{kasus.statusKeseluruhan}</Row>
              <Row label="Deskripsi"><span className="whitespace-pre-wrap">{kasus.deskripsi || "—"}</span></Row>
              <Row label="Tim Pemeriksa">
                <div className="flex flex-wrap gap-1">
                  {kasus.timPemeriksa.map((uid) => {
                    const u = users.find((x) => x.id === uid);
                    return (
                      <span key={uid} className="inline-flex items-center rounded-full border border-gray-200 bg-white px-2 py-0.5 text-[11px]">
                        {u?.name || uid}
                      </span>
                    );
                  })}
                  {kasus.timPemeriksa.length === 0 ? <span className="italic text-slate-400">—</span> : null}
                </div>
              </Row>
            </dl>
          </div>

          <div className="rounded-lg border border-gray-200 bg-white p-5">
            <h3 className="font-display text-base font-semibold text-slate-900">Countdown Deadline</h3>
            <div className="mt-3">
              <div className={`rounded-md p-3 text-center ${
                sisaHK < 0 ? "bg-slate-900 text-white" : sisaHK < 7 ? "bg-red-50 text-red-800" : sisaHK <= 15 ? "bg-amber-50 text-amber-800" : "bg-emerald-50 text-emerald-800"
              }`}>
                <div className="font-display text-4xl font-bold">
                  {sisaHK < 0 ? Math.abs(sisaHK) : sisaHK}
                </div>
                <div className="text-[11px] font-semibold uppercase tracking-wider">
                  {sisaHK < 0 ? "HK TERLEWAT" : "HK TERSISA"}
                </div>
              </div>
              <div className="mt-4">
                <div className="mb-1 text-[10.5px] font-semibold uppercase tracking-wider text-slate-500">Progres Rekomendasi</div>
                <div className="flex items-center gap-2">
                  <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200">
                    <div className="h-full bg-[#1e3a5f]" style={{ width: `${progressKasus(kasus)}%` }} />
                  </div>
                  <span className="text-[12px] font-semibold">{progressKasus(kasus)}%</span>
                </div>
              </div>
            </div>
          </div>
        </section>
      ) : null}

      {tab === "penyerahan" ? (
        <section className="space-y-5" data-testid="tab-penyerahan">
          <div className="rounded-lg border border-gray-200 bg-white p-5">
            <h3 className="font-display text-base font-semibold text-slate-900">Penyerahan Laporan ke Auditan</h3>
            <p className="mt-1 text-[13px] text-slate-500 mb-4">
              Pilih tanggal saat laporan resmi diserahkan kepada auditan/terperiksa. Deadline 60 Hari Kerja akan dihitung mulai dari tanggal ini, dan notifikasi WA otomatis akan dikirim ke Terperiksa & Tim Pemeriksa.
            </p>
            <div className="flex flex-col md:flex-row md:items-end gap-3">
              <label className="flex-1 max-w-sm">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Tanggal Penyerahan</div>
                <input
                  type="date"
                  disabled={!canEditTL}
                  value={kasus.tanggalPenyerahanLaporan || ""}
                  onChange={(e) => handleSetPenyerahan(e.target.value)}
                  className={input}
                />
              </label>
              {kasus.deadlineTL60HK && (
                <div className="flex-1 rounded-md border border-emerald-200 bg-emerald-50 px-4 py-2">
                  <div className="text-[11px] font-semibold text-emerald-800 uppercase">Deadline 60 HK Tindak Lanjut</div>
                  <div className="font-display text-lg font-bold text-emerald-900">{formatTanggalID(kasus.deadlineTL60HK)}</div>
                </div>
              )}
            </div>
            {kasus.notifPenyerahanSent && (
              <div className="mt-3 text-[12px] text-blue-600 font-medium flex items-center gap-1.5">
                <CheckCircle2 className="h-4 w-4" /> Notifikasi penyerahan telah terkirim ke pihak terkait.
              </div>
            )}
          </div>
        </section>
      ) : null}

      {tab === "rekomendasi" ? (
        <section className="space-y-3" data-testid="tab-rekomendasi">
          {kasus.rekomendasi.map((r, i) => (
            <div key={r.id} className="rounded-lg border border-gray-200 bg-white p-4" data-testid={`rek-card-${r.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 text-[10.5px] text-slate-500">
                    <span className="font-mono">#{i + 1}</span>
                    <span className="inline-flex items-center rounded bg-[#e8edf2] px-1.5 py-0.5 text-[9.5px] font-bold text-[#1e3a5f]">
                      {r.jenisTL}
                    </span>
                  </div>
                  <div className="mt-1 text-[13px] text-slate-900">{r.uraian}</div>
                </div>
                <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold ${STATUS_TL_COLOR[r.statusTL]}`}>
                  {STATUS_TL_LABEL[r.statusTL]}
                </span>
              </div>

              <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-3">
                {(r.jenisTL === JENIS_TL.UANG || r.jenisTL === JENIS_TL.KEDUANYA) ? (
                  <>
                    <div>
                      <div className="text-[10.5px] font-semibold uppercase tracking-wider text-slate-500">Nominal Temuan</div>
                      <div className="mt-0.5 font-mono text-[13px]">Rp {Number(r.nominalTemuan).toLocaleString("id-ID")}</div>
                    </div>
                    <label>
                      <div className="text-[10.5px] font-semibold uppercase tracking-wider text-slate-500">Nominal Kembali</div>
                      <input
                        type="number"
                        disabled={!canEditTL}
                        value={r.nominalKembali}
                        onChange={(e) => updateRek(r.id, { nominalKembali: Number(e.target.value) })}
                        className={input}
                        data-testid={`rek-${r.id}-kembali`}
                      />
                    </label>
                    <div>
                      <div className="text-[10.5px] font-semibold uppercase tracking-wider text-slate-500">Sisa Nominal</div>
                      <div className="mt-0.5 font-mono text-[13px] font-semibold text-red-700">Rp {Number(r.sisaNominal).toLocaleString("id-ID")}</div>
                    </div>
                  </>
                ) : null}
                <label className="md:col-span-2">
                  <div className="text-[10.5px] font-semibold uppercase tracking-wider text-slate-500">Status TL</div>
                  <select
                    disabled={!canEditTL}
                    value={r.statusTL}
                    onChange={(e) => updateRek(r.id, { statusTL: e.target.value })}
                    className={input}
                    data-testid={`rek-${r.id}-status`}
                  >
                    {Object.entries(STATUS_TL_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </label>
                <label className="md:col-span-3">
                  <div className="text-[10.5px] font-semibold uppercase tracking-wider text-slate-500">Catatan Perkembangan</div>
                  <textarea
                    disabled={!canEditTL}
                    value={r.catatan || ""}
                    onChange={(e) => updateRek(r.id, { catatan: e.target.value })}
                    rows={2}
                    className={`${input} resize-none`}
                  />
                </label>
                <div className="md:col-span-3 mt-2 border-t border-gray-100 pt-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-[10.5px] font-semibold uppercase tracking-wider text-slate-500">Bukti Tindak Lanjut (PDF/JPG)</div>
                    <label className="inline-flex cursor-pointer items-center gap-1 rounded-md border border-gray-200 bg-white px-2.5 py-1 text-[11px] font-semibold hover:bg-gray-50">
                      <Upload className="h-3.5 w-3.5" /> Upload Bukti
                      <input type="file" accept=".pdf,image/jpeg,image/png" className="hidden" onChange={(e) => handleUploadBukti(r.id, e)} disabled={!canEditTL} />
                    </label>
                  </div>
                  {(r.buktiFiles && r.buktiFiles.length > 0) ? (
                     <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                       {r.buktiFiles.map(b => (
                         <div key={b.id} className="flex items-center justify-between rounded border border-gray-200 p-2 text-[11.5px]">
                           <a href={b.dataUrl} download={b.name} className="truncate text-[#1e3a5f] hover:underline" title={b.name}>
                             {b.name}
                           </a>
                           {canEditTL && (
                             <button type="button" onClick={() => hapusBukti(r.id, b.id)} className="ml-2 text-red-500 hover:text-red-700">✕</button>
                           )}
                         </div>
                       ))}
                     </div>
                  ) : (
                    <div className="text-[11.5px] text-slate-400 italic">Belum ada bukti yang diunggah.</div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </section>
      ) : null}

      {tab === "terperiksa" ? (
        <section className="space-y-6" data-testid="tab-terperiksa">
          {/* Bagian Tim Pemeriksa */}
          <div className="rounded-lg border border-gray-200 bg-white p-4">
             <h3 className="font-display text-sm font-semibold text-slate-900 border-b border-gray-100 pb-2 mb-3">Tim Pemeriksa (Internal)</h3>
             <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
               {kasus.timPemeriksa.map(uid => {
                 const u = users.find(x => x.id === uid);
                 if (!u) return null;
                 return (
                   <div key={u.id} className="rounded border border-gray-100 bg-gray-50 p-3">
                     <div className="text-[11px] font-semibold text-[#1e3a5f] uppercase mb-1">{u.role?.replace("_", " ")}</div>
                     <div className="text-[13px] font-bold text-slate-800 mb-2">{u.name}</div>
                     <label className="block">
                       <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">No. WhatsApp</div>
                       <div className="flex gap-2">
                         <input
                           type="text"
                           defaultValue={u.noWA || ""}
                           placeholder="Mis. 08123456789"
                           className={`${input} py-1.5 text-[12px]`}
                           onBlur={async (e) => {
                             const newVal = e.target.value.trim();
                             if (newVal !== (u.noWA || "")) {
                               try {
                                 await api.put(`/users/${u.id}/wa`, { noWA: newVal });
                                 // update local users state so it reflects immediately without reload
                                 setUsers(prev => prev.map(x => x.id === u.id ? { ...x, noWA: newVal } : x));
                                 toast.success(`Nomor WA ${u.name} berhasil disimpan`);
                               } catch (err) {
                                 toast.error("Gagal menyimpan WA: " + err.message);
                               }
                             }
                           }}
                           disabled={!canEditKontak}
                         />
                       </div>
                     </label>
                   </div>
                 );
               })}
               {kasus.timPemeriksa.length === 0 && <div className="text-[12px] italic text-slate-400">Belum ada tim pemeriksa ditugaskan.</div>}
             </div>
             {!canEditKontak && <div className="mt-3 text-[10.5px] text-slate-400">Hanya IRBAN/DALNIS yang dapat mengubah data kontak tim.</div>}
          </div>

          {/* Bagian Terperiksa */}
          <div className="rounded-lg border border-gray-200 bg-white p-4">
             <h3 className="font-display text-sm font-semibold text-slate-900 border-b border-gray-100 pb-2 mb-3">Terperiksa (Auditan)</h3>
             <div className="space-y-3">
               {kasus.terperiksa.map((t, i) => (
                 <div key={i} className="rounded border border-gray-100 bg-gray-50 p-3" data-testid={`terperiksa-${i}`}>
                   <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                     <DetailField label="Nama Pejabat">{t.namaPejabat || "—"}</DetailField>
                     <DetailField label="Jabatan">{t.jabatan || "—"}</DetailField>
                     <DetailField label="Instansi">{t.namaInstansi || "—"}</DetailField>
                     <DetailField label="Peran dalam Kasus">{t.peranDalamKasus || "—"}</DetailField>
                     <DetailField label="No. WhatsApp"><span className="font-mono">{t.noWA || "—"}</span></DetailField>
                     <DetailField label="Email"><span className="font-mono">{t.email || "—"}</span></DetailField>
                   </div>
                 </div>
               ))}
             </div>
             {!canEditKontak ? <div className="mt-3 text-[10.5px] text-slate-400">Hanya IRBAN/DALNIS yang dapat mengubah data kontak.</div> : null}
          </div>
        </section>
      ) : null}

      {tab === "riwayat" ? (
        <section data-testid="tab-riwayat">
          {(kasus.riwayatNotifikasi || []).length === 0 ? (
            <div className="rounded-md border border-dashed border-gray-300 bg-white px-4 py-10 text-center">
              <div className="text-[12.5px] font-semibold text-slate-700">Belum ada riwayat notifikasi.</div>
              <div className="mt-1 text-[11.5px] text-slate-500">Klik "Run Engine 60HK" untuk generate notifikasi otomatis.</div>
            </div>
          ) : (
            <ol className="space-y-2">
              {kasus.riwayatNotifikasi.map((r) => (
                <li key={r.id} data-testid={`riwayat-${r.id}`} className="rounded-md border border-gray-200 bg-white p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 text-[10.5px]">
                        <span className="inline-flex items-center rounded-full border border-[#1e3a5f]/30 bg-[#e8edf2] px-1.5 py-0.5 font-semibold uppercase tracking-wider text-[#1e3a5f]">
                          {r.jenis}
                        </span>
                        <span className={`inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 font-semibold ${
                          r.media === "WA" ? "border-green-200 bg-green-50 text-green-700"
                          : r.media === "EMAIL" ? "border-blue-200 bg-blue-50 text-blue-700"
                          : "border-slate-200 bg-slate-50 text-slate-700"
                        }`}>
                          {r.media === "WA" ? <MessageCircle className="h-3 w-3" /> : <Mail className="h-3 w-3" />}
                          {r.media}
                        </span>
                        <span className={`inline-flex items-center rounded-full border px-1.5 py-0.5 font-semibold ${
                          r.statusKirim === "TERKIRIM" ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                          : r.statusKirim === "GAGAL" ? "border-red-200 bg-red-50 text-red-700"
                          : r.statusKirim === "DRAFT_READY" ? "border-amber-200 bg-amber-50 text-amber-700"
                          : "border-gray-200 bg-gray-50 text-slate-700"
                        }`}>
                          {r.statusKirim}
                        </span>
                      </div>
                      <div className="mt-1 text-[12.5px] font-medium text-slate-800">{r.penerima}</div>
                      <div className="mt-0.5 font-mono text-[10.5px] text-slate-500">{r.target}</div>
                      {r.pesanTerkirim ? (
                        <div className="mt-2 whitespace-pre-wrap rounded bg-slate-50 p-2 text-[11.5px] text-slate-700">
                          {r.pesanTerkirim}
                        </div>
                      ) : null}
                      {r.errorMessage ? <div className="mt-1 text-[11px] text-red-600">⚠ {r.errorMessage}</div> : null}
                    </div>
                    <div className="flex shrink-0 flex-col gap-1">
                      {r.media === "WA" && canSendManual ? (
                        <button
                          onClick={() => sendWAEntry(r)}
                          data-testid={`send-${r.id}`}
                          className="inline-flex items-center gap-1 rounded-md bg-[#1e3a5f] px-2 py-1 text-[10.5px] font-semibold text-white hover:bg-[#152a46]"
                        >
                          <Send className="h-3 w-3" />
                          {r.statusKirim === "GAGAL" ? "Ulang" : "Kirim"}
                        </button>
                      ) : null}
                      {r.media === "EMAIL" ? (
                        <button
                          onClick={() => openDraft(r)}
                          data-testid={`draft-${r.id}`}
                          className="inline-flex items-center gap-1 rounded-md border border-gray-200 px-2 py-1 text-[10.5px] font-semibold hover:border-[#1e3a5f]"
                        >
                          <Copy className="h-3 w-3" /> Draft
                        </button>
                      ) : null}
                      <button
                        onClick={() => handleExportPDF(r)}
                        title="Export sbg PDF / Print"
                        className="inline-flex items-center gap-1 rounded-md border border-gray-200 px-2 py-1 text-[10.5px] font-semibold text-slate-600 hover:border-[#1e3a5f] hover:text-[#1e3a5f]"
                      >
                        <Printer className="h-3 w-3" /> PDF
                      </button>
                      {canSendManual && (
                        <button
                          onClick={() => handleDeleteNotif(r.id)}
                          title="Hapus Notifikasi"
                          className="inline-flex items-center gap-1 rounded-md border border-red-200 bg-red-50 px-2 py-1 text-[10.5px] font-semibold text-red-600 hover:bg-red-100 hover:text-red-700"
                        >
                          <Trash2 className="h-3 w-3" /> Hapus
                        </button>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ol>
          )}
        </section>
      ) : null}

      {tab === "kirim_notif" ? (
        <section className="space-y-5" data-testid="tab-kirim-notif">
          <div className="rounded-lg border border-gray-200 bg-white p-5">
            <h3 className="font-display text-base font-semibold text-slate-900 mb-4">Kirim Pemberitahuan Baru</h3>
            <form onSubmit={handleGenerateNotif} className="space-y-4">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="rounded border border-gray-200 p-4">
                  <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-3">Tipe Pemberitahuan</div>
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input 
                        type="radio" 
                        name="tipe_notif" 
                        checked={notifForm.tipe === "MANUAL"} 
                        onChange={() => setNotifForm({ ...notifForm, tipe: "MANUAL" })}
                        className="text-[#1e3a5f] focus:ring-[#1e3a5f]"
                      />
                      <span className="text-[13px] text-slate-800">Manual (Sesuai Keinginan)</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input 
                        type="radio" 
                        name="tipe_notif" 
                        checked={notifForm.tipe === "BERKALA"} 
                        onChange={() => setNotifForm({ ...notifForm, tipe: "BERKALA" })}
                        className="text-[#1e3a5f] focus:ring-[#1e3a5f]"
                      />
                      <span className="text-[13px] text-slate-800">Berkala (3 Kali dalam 60 HK)</span>
                    </label>
                  </div>
                </div>

                <div className="rounded border border-gray-200 p-4">
                  <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-3">Target Penerima</div>
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input 
                        type="checkbox" 
                        checked={notifForm.targetTim} 
                        onChange={(e) => setNotifForm({ ...notifForm, targetTim: e.target.checked })}
                        className="rounded text-[#1e3a5f] focus:ring-[#1e3a5f]"
                      />
                      <span className="text-[13px] text-slate-800">Tim Pemeriksa Terkait</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input 
                        type="checkbox" 
                        checked={notifForm.targetTerperiksa} 
                        onChange={(e) => setNotifForm({ ...notifForm, targetTerperiksa: e.target.checked })}
                        className="rounded text-[#1e3a5f] focus:ring-[#1e3a5f]"
                      />
                      <span className="text-[13px] text-slate-800">Pihak Terperiksa</span>
                    </label>
                  </div>
                </div>
              </div>

              {notifForm.tipe === "MANUAL" && (
                <div>
                  <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Pesan Khusus</div>
                  <textarea 
                    value={notifForm.pesan}
                    onChange={(e) => setNotifForm({ ...notifForm, pesan: e.target.value })}
                    rows={4}
                    placeholder="Ketik pesan WhatsApp yang ingin dikirim..."
                    className={`${input} resize-none`}
                  />
                  <p className="mt-1 text-[11px] text-slate-400">Pesan ini akan dikirim persis seperti yang Anda ketikkan.</p>
                </div>
              )}
              
              {notifForm.tipe === "BERKALA" && (
                <div className="rounded bg-blue-50 border border-blue-200 p-3">
                  <div className="text-[12px] font-medium text-blue-800">
                    Sistem akan membuat 3 draf pesan notifikasi yang ditujukan kepada target terpilih. Anda dapat mengirimkan draf tersebut secara berurutan melalui tab <b>Riwayat Notifikasi</b>.
                  </div>
                </div>
              )}

              <div className="pt-2">
                <button
                  type="submit"
                  disabled={!canSendManual}
                  className="inline-flex items-center gap-1.5 rounded-md bg-[#1e3a5f] px-4 py-2 text-[13px] font-semibold text-white hover:bg-[#152a46] disabled:opacity-50"
                >
                  <Send className="h-4 w-4" /> Generate Pemberitahuan
                </button>
                {!canSendManual && <span className="ml-3 text-[11px] text-red-500">Anda tidak memiliki akses untuk mengirim notifikasi.</span>}
              </div>
            </form>
          </div>
        </section>
      ) : null}

      {/* Email draft modal */}
      {draftModal ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="email-draft-modal">
          <div className="w-full max-w-xl rounded-lg bg-white p-5 shadow-xl">
            <div className="flex items-center justify-between">
              <h3 className="font-display text-base font-bold text-slate-900">Draft Email</h3>
              <button onClick={() => setDraftModal(null)} className="text-slate-500 hover:text-slate-900">✕</button>
            </div>
            <div className="mt-3 space-y-2">
              <div>
                <div className="text-[10.5px] font-semibold uppercase tracking-wider text-slate-500">Subject</div>
                <input type="text" readOnly value={draftModal.draft.subject} className={`${input} bg-gray-50`} data-testid="draft-subject" />
              </div>
              <div>
                <div className="text-[10.5px] font-semibold uppercase tracking-wider text-slate-500">Body</div>
                <textarea readOnly value={draftModal.draft.body} rows={10} className={`${input} resize-none bg-gray-50 font-mono text-[11.5px]`} data-testid="draft-body" />
              </div>
              <div className="flex flex-wrap items-center gap-2 pt-2">
                <button
                  onClick={() => { navigator.clipboard.writeText(draftModal.draft.subject + "\n\n" + draftModal.draft.body); toast.success("Draft disalin ke clipboard"); }}
                  className="inline-flex items-center gap-1.5 rounded-md bg-[#1e3a5f] px-3 py-1.5 text-[12px] font-semibold text-white hover:bg-[#152a46]"
                  data-testid="copy-draft-btn"
                >
                  <Copy className="h-3.5 w-3.5" /> Copy Draft
                </button>
                <a
                  href={draftModal.draft.mailto}
                  className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 px-3 py-1.5 text-[12px] font-semibold hover:border-[#1e3a5f]"
                >
                  <Mail className="h-3.5 w-3.5" /> Buka Email Client
                </a>
                <button
                  onClick={() => { updateRiwayat(draftModal.entry.id, { statusKirim: "TERKIRIM", tanggalKirim: new Date().toISOString() }); toast.success("Ditandai Terkirim"); setDraftModal(null); }}
                  className="ml-auto inline-flex items-center gap-1.5 rounded-md bg-emerald-600 px-3 py-1.5 text-[12px] font-semibold text-white hover:bg-emerald-700"
                  data-testid="mark-sent-btn"
                >
                  <CheckCircle2 className="h-3.5 w-3.5" /> Tandai Terkirim
                </button>
              </div>
              <p className="text-[10.5px] text-slate-400">
                {/* TODO-PROD: integrasi SMTP via backend /api/email/send */}
                Mode prototype: kirim email manual via email client Anda, lalu tandai Terkirim.
              </p>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div className="flex flex-col gap-0.5 border-b border-gray-100 py-1.5 last:border-b-0 md:flex-row md:items-center md:gap-3">
      <dt className="shrink-0 basis-[160px] text-[10.5px] font-semibold uppercase tracking-wider text-slate-500">{label}</dt>
      <dd className="text-[12.5px] text-slate-800">{children}</dd>
    </div>
  );
}

function DetailField({ label, children }) {
  return (
    <div>
      <div className="text-[10.5px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
      <div className="mt-0.5 text-[12.5px] text-slate-800">{children}</div>
    </div>
  );
}
